import { z } from 'zod';
import { OWNER_ALERT_TYPES } from './sync';
const totpCodeSchema = z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator');
export const OWNER_MAIL_PAGE_SIZE = 25;
export const OWNER_MAIL_MAX_PAGE = 400;
export const OWNER_MAIL_MAX_RECIPIENTS = 10;
export const OWNER_MAIL_MAX_MESSAGE_BYTES = 20 * 1024 * 1024;
export const OWNER_MAIL_MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const mailFolderSchema = z.string().trim().min(1).max(120);
export const ownerMailListQuerySchema = z.object({
    folder: mailFolderSchema.default('INBOX'),
    page: z.coerce.number().int().min(1).max(OWNER_MAIL_MAX_PAGE).default(1),
    refresh: z.enum(['1', 'true']).optional(),
});
export const ownerMailMessageQuerySchema = z.object({ folder: mailFolderSchema.default('INBOX') });
export const ownerMailUidSchema = z.coerce.number().int().min(1).max(4_294_967_295);
export const ownerMailAttachmentIndexSchema = z.coerce.number().int().min(0).max(200);
export const ownerMailSendBodySchema = z.object({
    to: z.array(z.string().trim().email().max(254)).min(1).max(OWNER_MAIL_MAX_RECIPIENTS),
    cc: z.array(z.string().trim().email().max(254)).max(OWNER_MAIL_MAX_RECIPIENTS).default([]),
    subject: z.string().trim().min(1).max(300),
    text: z.string().min(1).max(100_000),
    inReplyTo: z.string().trim().max(500).optional(),
});
export const ownerMailSendRequestSchema = ownerMailSendBodySchema.extend({ totpCode: totpCodeSchema });
export const ALERT_EMAIL_ROUTES = ['owner', 'customer', 'both', 'none'];
export const ALERT_EMAIL_HELD_REASONS = ['EMAIL_OFF', 'ROUTE_NONE', 'TENANT_MUTED', 'REPEAT_WINDOW'];
export const OWNER_ALERT_DEDUPE_MAX_HOURS = 720;
export const OWNER_ALERT_MUTES_MAX = 200;
export const DEFAULT_ALERT_DEDUPE_HOURS = 6;
export const OWNER_ALERT_EMAIL_STATS_DAYS = 30;
export const CUSTOMER_ONLY_ALERT_TYPES = ['NEW_VERSION', 'SUBSCRIPTION_ENDING', 'CLOSURE_SCHEDULED'];
export const OWNER_ONLY_ALERT_TYPES = ['BRANCH_REQUESTED'];
const DEFAULT_DEDUPE_HOURS = {
    NEW_VERSION: 0,
    STEP_UP_LOCKOUT: 0,
    OWNER_LOGIN_ATTACK: 0,
    SUPPORT_SESSION_OPENED: 0,
    CLOSURE_SCHEDULED: 0,
    EXPORT_READY: 0,
    LIFECYCLE_REPORT: 0,
    BRANCH_REQUESTED: 0,
    CUSTOMER_SILENT: 24,
    SUBSCRIPTION_ENDING: 24,
};
export const alertEmailRuleSchema = z.object({
    type: z.enum(OWNER_ALERT_TYPES),
    route: z.enum(ALERT_EMAIL_ROUTES),
    dedupeHours: z.number().int().min(0).max(OWNER_ALERT_DEDUPE_MAX_HOURS),
});
export const alertEmailMuteSchema = z.object({
    tenantId: z.string().trim().min(1).max(64),
    until: z.string().datetime().nullable(),
});
export const ownerAlertEmailPolicySchema = z.object({
    enabled: z.boolean(),
    rules: z.array(alertEmailRuleSchema).max(OWNER_ALERT_TYPES.length),
    mutes: z.array(alertEmailMuteSchema).max(OWNER_ALERT_MUTES_MAX),
});
export const ownerAlertEmailPolicyRequestSchema = ownerAlertEmailPolicySchema.extend({ totpCode: totpCodeSchema });
export function isCustomerOnlyAlert(type) {
    return CUSTOMER_ONLY_ALERT_TYPES.includes(type);
}
export function defaultAlertEmailRoute(type, notifyCustomerAdmin) {
    if (isCustomerOnlyAlert(type))
        return 'customer';
    if (OWNER_ONLY_ALERT_TYPES.includes(type))
        return 'owner';
    return notifyCustomerAdmin ? 'both' : 'owner';
}
export function defaultAlertEmailRule(type, notifyCustomerAdmin) {
    return { type, route: defaultAlertEmailRoute(type, notifyCustomerAdmin), dedupeHours: DEFAULT_DEDUPE_HOURS[type] ?? DEFAULT_ALERT_DEDUPE_HOURS };
}
export function defaultOwnerAlertEmailPolicy(notifyCustomerAdmin) {
    return { enabled: true, rules: OWNER_ALERT_TYPES.map((type) => defaultAlertEmailRule(type, notifyCustomerAdmin)), mutes: [] };
}
export function resolveOwnerAlertEmailPolicy(raw, notifyCustomerAdmin) {
    const defaults = defaultOwnerAlertEmailPolicy(notifyCustomerAdmin);
    const parsed = ownerAlertEmailPolicySchema.partial().safeParse(raw);
    if (!parsed.success)
        return defaults;
    const stored = new Map((parsed.data.rules ?? []).map((rule) => [rule.type, rule]));
    return {
        enabled: parsed.data.enabled ?? defaults.enabled,
        rules: defaults.rules.map((rule) => stored.get(rule.type) ?? rule),
        mutes: parsed.data.mutes ?? defaults.mutes,
    };
}
export function alertEmailRuleFor(policy, type) {
    return policy.rules.find((rule) => rule.type === type) ?? defaultAlertEmailRule(type, true);
}
export function activeAlertMuteFor(policy, tenantId, now) {
    const mute = policy.mutes.find((candidate) => candidate.tenantId === tenantId);
    if (!mute)
        return null;
    if (mute.until === null)
        return mute;
    return new Date(mute.until).getTime() > now.getTime() ? mute : null;
}
//# sourceMappingURL=owner-mail.js.map